export function isValidIanaTimeZone(value: string) {
  if (!value.trim()) return false;
  try {
    new Intl.DateTimeFormat("it-IT", { timeZone: value }).format(new Date(0));
    return true;
  } catch {
    return false;
  }
}

export function normalizeTimeZone(value: string | null | undefined) {
  return value && isValidIanaTimeZone(value) ? value : "UTC";
}

/**
 * Scarto in minuti fra il fuso indicato e UTC, per un preciso istante.
 * Si misura formattando l'istante in quel fuso e rileggendo il risultato come se fosse UTC:
 * la differenza fra i due numeri e lo scarto, ora legale compresa.
 */
export function timeZoneOffsetMinutes(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: normalizeTimeZone(timeZone),
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant);
  const value = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value ?? "0");
  const asUtc = Date.UTC(
    value("year"),
    value("month") - 1,
    value("day"),
    value("hour"),
    value("minute"),
    value("second"),
  );
  return Math.round((asUtc - instant.getTime()) / 60_000);
}

/**
 * L'istante vero di un orario da calendario: giorno `AAAA-MM-GG` piu minuti dalla mezzanotte,
 * letti nel fuso indicato.
 *
 * Serve al programma settimanale: "il martedi alle 8" deve restare le 8 anche dopo il cambio
 * dell'ora, e il cliente lavora esattamente a cavallo dei due cambi, marzo e ottobre. Il calcolo
 * si fa in due passaggi perche lo scarto dipende dall'istante che stiamo cercando: il primo
 * passaggio da una stima, il secondo la corregge sul lato giusto del cambio.
 */
export function zonedWallClockToUtc(dateKey: string, minutesFromMidnight: number, timeZone: string): Date {
  const [year, month, day] = dateKey.split("-").map(Number);
  const wallClock = Date.UTC(year, month - 1, day, 0, minutesFromMidnight);
  const firstGuess = new Date(wallClock - timeZoneOffsetMinutes(new Date(wallClock), timeZone) * 60_000);
  return new Date(wallClock - timeZoneOffsetMinutes(firstGuess, timeZone) * 60_000);
}

/** Il giorno di calendario, `AAAA-MM-GG`, in cui quell'istante cade nel fuso indicato. */
export function dateKeyInZone(instant: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: normalizeTimeZone(timeZone),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(instant);
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "";
  return `${value("year")}-${value("month")}-${value("day")}`;
}
